import {
	Avatar,
	Box,
	Card,
	Grid,
	CardActions,
	CardContent,
	CardMedia,
	StepConnector,
	Typography,
	useTheme,
} from '@mui/material';

import { Maybe } from '@/types';
import { Card as TCard } from '@/types/generated';
import { ContentTypeRichTextContent } from '@/graphql/__generated/sdk';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import TAGSvgIcon, { IconName } from '@/components/TAGSvgIcon';
import TermsAndConditions from '@/components/TermsAndConditions';
import { textToJumpLink } from '@/utils';
import ContentfulButton from '@/components/ContentfulButton';
import ImageAsset from '@/components/ImageAsset';
import { IImage } from '@/components/ImageAsset/ImageAsset';
import { getCardButtonAriaLabel } from '@/components/MultiCard/MultiCard.helpers';

import {
	getCardStyles,
	getCardActionStyles,
	getCardContentStyles,
	getAvatarStyles,
	getStepperCardBoxStyles,
	getCardMediaStyles,
	cardDescriptonStyles,
} from './CardItem.styles';

interface ICardItem {
	card: TCard;
	isSmallScreen: boolean;
	isDarkBackground?: boolean;
	displayAsStepper?: boolean;
	showLeftConnector?: boolean;
	showRightConnector?: boolean;
	enableResourceCardMode?: boolean;
	useMediaRoundedCorners?: boolean;
	variant?: Maybe<string>;
}

export default function CardItem({
	card,
	isDarkBackground,
	isSmallScreen,
	displayAsStepper = false,
	showLeftConnector,
	showRightConnector,
	useMediaRoundedCorners = false,
	variant,
}: ICardItem) {
	const isDarkWrapperBackground = isDarkBackground || false;
	const theme = useTheme();
	const hasImageOrIcon = Boolean(card.cardIcon || card.imageAsset);
	const cardButtons = card?.buttonsCollection?.items;
	const iconColor = isDarkWrapperBackground ? 'neutrals.white' : 'tertiary.main';

	return (
		<Card
			data-testid={`group_card_${card.sys.id}`}
			id={textToJumpLink(card.title)}
			sx={getCardStyles({
				backgroundColor: card.backgroundColor,
				isDarkWrapperBackground,
				displayAsStepper,
				isSmallScreen,
			})}
		>
			{displayAsStepper ? (
				<Box
					sx={getStepperCardBoxStyles(isSmallScreen)}
					data-testid={`multicard_as_stepper_step_item_${card.sys.id}`}
				>
					{!isSmallScreen && <StepConnector sx={{ visibility: showLeftConnector ? 'visible' : 'hidden' }} />}
					<Avatar sx={getAvatarStyles(theme, hasImageOrIcon)}>
						{card.imageAsset ? (
							<ImageAsset
								desktopImage={card.imageAsset.desktopImage as IImage}
								mobileImage={card.imageAsset.mobileImage as IImage}
								isBackgroundImage={true}
								dataTestId={`multicard_as_stepper_avatar_image_asset_${card.sys.id}`}
							/>
						) : card.cardIcon ? (
							<TAGSvgIcon
								width="60"
								height="60"
								icon={card.cardIcon as IconName}
								htmlColor={iconColor}
								dataTestId={`multicard_as_stepper_avatar_icon_${card.sys.id}`}
							/>
						) : null}
					</Avatar>
					<StepConnector sx={{ visibility: showRightConnector ? 'visible' : 'hidden' }} />
				</Box>
			) : null}

			{!displayAsStepper && card.imageAsset && (
				<CardMedia
					component={card.imageAsset && card.buttonUrl ? 'a' : 'div'}
					href={card.buttonUrl || undefined}
					data-testid={`group_card_image_asset_link_${card.sys.id}`}
					sx={{ ...getCardMediaStyles(useMediaRoundedCorners), overflow: 'hidden' }}
				>
					<ImageAsset
						desktopImage={card.imageAsset.desktopImage as IImage}
						mobileImage={card.imageAsset.mobileImage as IImage}
						isBackgroundImage={false}
						dataTestId={`img_asset_card_item_background_${card.sys.id}`}
						imageSx={
							variant === 'Leadership'
								? { borderRadius: '250px', width: '90%', height: '90%', minHeight: '0', minWidth: '0' }
								: null
						}
					/>
				</CardMedia>
			)}

			{/* To Do : remove this once transition to brandfolder imageAssets is in place  */}

			{!displayAsStepper && !card.imageAsset && card.image?.url && (
				<CardMedia
					component={'img'}
					{...(card.imageUrl ? { href: card.imageUrl } : {})}
					src={card.image?.url}
					data-testid={`group_card_image_${card.sys.id}`}
					sx={{ ...getCardMediaStyles(useMediaRoundedCorners), overflow: 'hidden' }}
				/>
			)}

			{card.cardIcon || card.eyebrow || card.title || card.description || card.richTextDescription ? (
				<CardContent
					sx={getCardContentStyles(isSmallScreen, displayAsStepper, card.cardTextAlignment, variant)}
				>
					{card.cardIcon && !displayAsStepper && (
						<Grid
							item
							sx={{
								display: 'flex',
								justifyContent: card.cardIconAlignment,
								width: '100%',
							}}
						>
							<TAGSvgIcon
								size={64}
								htmlColor={iconColor}
								icon={card.cardIcon as IconName}
								dataTestId={`card_icon_${card.cardIcon}`}
							/>
						</Grid>
					)}
					{card.eyebrow && (
						<Typography
							lineHeight="1rem"
							fontSize="0.875rem"
							fontFamily="Messina Sans"
							color={
								isDarkBackground && card.backgroundColor === 'transparent'
									? 'text.light'
									: 'text.secondary'
							}
							variant={isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook'}
							data-testid={`text_card_eyebrow_${card.sys.id}`}
						>
							{card.eyebrow}
						</Typography>
					)}
					{card.title && (
						<Typography
							variant="bodyXLBook"
							color={
								isDarkBackground && card.backgroundColor === 'transparent'
									? 'text.light'
									: 'text.primary'
							}
							sx={{ fontFamily: 'Messina Modern' }}
							data-testid={`text_card_title_${card.sys.id}`}
						>
							{card.title}
						</Typography>
					)}
					{card.description && (
						<Typography
							color={
								isDarkBackground && card.backgroundColor === 'transparent'
									? 'text.light'
									: 'text.secondary'
							}
							variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}
							data-testid={`text_card_description_${card.sys.id}`}
							textAlign={
								displayAsStepper && isSmallScreen
									? 'left'
									: card.cardTextAlignment === 'center'
									? 'center'
									: 'left'
							}
						>
							{card.description}
						</Typography>
					)}
					{card.richTextDescription && (
						<ContentTypeRichText
							content={card.richTextDescription as ContentTypeRichTextContent}
							dataTestId="card_content_text_richtext"
							contentMaxWidth="100%"
							disableGutters={true}
							disablePaddingOn={['Top', 'Bottom']}
							containerSx={cardDescriptonStyles()}
							backgroundColor="transparent"
							docProps={{
								fontWeight: 700,
								fontSize: isSmallScreen ? '1.25rem' : '1.04vw',
								letterSpacing: '-0.5px',
								fontFamily: 'Messina Sans',
								color: 'text.light',
							}}
						/>
					)}
				</CardContent>
			) : null}

			{/* button collection should be supported in future uses */}
			{cardButtons && cardButtons.length > 0
				? cardButtons.map((button) =>
						button ? (
							<CardActions
								key={button?.sys?.id}
								sx={getCardActionStyles({
									isSmallScreen,
									cardActionAlignment: card.cardTextAlignment,
									displayAsStepper,
								})}
							>
								<ContentfulButton
									{...button}
									defaultVariant="secondaryDefault"
									dataTestId={`group_card_button_${button?.name || ''}`}
									docProps={{ fontSize: '12px', color: '#289AE2', fontFamily: 'Messina Sans' }}
									ariaLabel={getCardButtonAriaLabel(button?.label, card?.title)}
								/>
							</CardActions>
						) : null
				  )
				: null}

			{/* TODO: deprecate button and use button collections above while we temporarily support buttonUrl */}
			{card.buttonUrl && (
				<CardActions
					sx={getCardActionStyles({
						isSmallScreen,
						cardActionAlignment: card.cardTextAlignment,
						displayAsStepper,
					})}
				>
					<ContentfulButton
						_id={card.sys.id}
						sys={card.sys}
						buttonLink={card.buttonUrl}
						label={card.buttonText}
						defaultVariant="secondaryDefault"
						variant={card.buttonVariant}
						icon={card.buttonIcon}
						iconPosition={card.buttonIconPosition}
						iconSize={card.buttonIconSize}
						size={card.buttonSize || 'medium'}
						dataTestId="button_card_action"
						contentfulMetadata={card.contentfulMetadata}
						analyticsContext={card.analyticsContext}
						ariaLabel={getCardButtonAriaLabel(card.buttonText, card.title)}
					/>
				</CardActions>
			)}

			{card.tc && (
				<Box
					position="relative"
					paddingX={isSmallScreen ? '1rem' : 0}
					data-testid={`group_card_terms_and_conditions_wrapper_${card.sys.id}`}
				>
					<TermsAndConditions {...card.tc} textSx={{ textAlign: card.cardTextAlignment || 'left' }} />
				</Box>
			)}
		</Card>
	);
}
